import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPut } from "@vimaana/ui";

import type { Barzahlung } from "../lib/types";

/**
 * Projekt vor Ort bar bezahlt, ohne Rechnung (Benjamin, 07.10.2026 — Migration 207).
 *
 * Schlüssel unter ["projekt", id], damit Lage, Auftragswert und Kopf mitziehen.
 * Nach dem Speichern ändern sich außerdem Angebote (gestellt → angenommen),
 * die Projektliste, die Aufgaben („nachfassen" fällt weg) und der Umsatz.
 */
export function useBarzahlung(projektId: string, enabled = true) {
  return useQuery<Barzahlung | null>({
    queryKey: ["projekt", projektId, "barzahlung"],
    queryFn: () => apiGet<Barzahlung | null>(`/api/kontor/projekt/${projektId}/barzahlung`),
    enabled: enabled && !!projektId,
    staleTime: 30_000,
  });
}

export type BarzahlungBody = {
  brutto: number;
  zahlungsdatum: string;
  ust_satz: number;
  empfaenger_owner_id: string;
  vermerk: string;
};

type Antwort = { barzahlung: Barzahlung | null; auftragswert: number; angebote_angenommen: string[] };

function useNachziehen(projektId: string) {
  const qc = useQueryClient();
  return () => {
    for (const key of [["projekt", projektId], ["projekte"], ["invoices"], ["notizen"], ["finanzen"]]) {
      qc.invalidateQueries({ queryKey: key });
    }
  };
}

export function useBarzahlungSpeichern(projektId: string) {
  const nachziehen = useNachziehen(projektId);
  return useMutation({
    mutationFn: (body: BarzahlungBody) => apiPut<Antwort>(`/api/kontor/projekt/${projektId}/barzahlung`, body),
    onSuccess: nachziehen,
  });
}

export function useBarzahlungEntfernen(projektId: string) {
  const nachziehen = useNachziehen(projektId);
  return useMutation({
    mutationFn: () => apiDelete<Antwort>(`/api/kontor/projekt/${projektId}/barzahlung`),
    onSuccess: nachziehen,
  });
}
